import type { CSSProperties } from 'react';
import type { PortalProps } from './const';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import { createPortal } from 'react-dom';

function Portal(props: PortalProps, ref: React.Ref<HTMLElement>) {
  const { children, className, style, id, getContainer } = props;
  const containerRef = useRef<HTMLElement | null>(null);
  const initRef = useRef<boolean>(false);

  if (!initRef.current) {
    if (getContainer) {
      containerRef.current = getContainer();
    } else {
      const element = document.createElement('div');
      if (id) {
        element.id = id;
      }
      containerRef.current = element;
      document.body.appendChild(element);
    }

    className && containerRef.current.classList.add(className);
    if (style) {
      const keys = Object.keys(style);
      for (const key of keys) {
        containerRef.current.style[key] = style[key as keyof CSSProperties];
      }
    }
    initRef.current = true;
  }

  useImperativeHandle(ref, () => containerRef.current as HTMLElement, []);

  useEffect(() => {
    return () => {
      containerRef.current?.remove();
      containerRef.current = null;
      initRef.current = false;
    };
  }, []);

  return containerRef.current ? createPortal(children, containerRef.current) : null;
}

const PortalComponent = forwardRef<HTMLElement, PortalProps>(Portal);

PortalComponent.displayName = 'Portal';

export default PortalComponent;
